import React, { useEffect, useRef } from 'react';
import { diamondPoints, prefersReducedMotion } from '../../utils';

// Concentric diamond outlines drawn from the logo's geometry (center 340,340;
// a ring at value v spans v → 680-v). Outer rings are faint; inner rings brighten.
const RINGS = [40, 92, 144, 196, 248, 300];
const baseOpacity = (i: number) => 0.08 + (i / (RINGS.length - 1)) * 0.16;

type RippleItem = { el: SVGPolygonElement | null; isCore: boolean; ringIndex: number };

/**
 * Ambient hero signature built from the Novaeon mark:
 *  - cursor parallax: inner rings drift more than outer ones (aperture of depth)
 *  - click ripple: a white pulse travels core → rim, brightening and briefly
 *    magnifying each element as it passes, then settling back.
 * Parallax + scale are composed in one rAF loop; disabled under reduced-motion.
 */
const HeroSignature: React.FC = () => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const ringRefs = useRef<Array<SVGPolygonElement | null>>([]);
  const coreRef = useRef<SVGPolygonElement | null>(null);
  const target = useRef({ x: 0, y: 0 });
  const ringOff = useRef(RINGS.map(() => ({ x: 0, y: 0 })));
  const coreOff = useRef({ x: 0, y: 0 });
  const history = useRef<Array<{ x: number; y: number }>>([]);
  const ringScale = useRef<number[]>(RINGS.map(() => 1));
  const ringScaleTarget = useRef<number[]>(RINGS.map(() => 1));
  const coreScale = useRef(1);
  const coreScaleTarget = useRef(1);
  const rippling = useRef(false);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    const clamp = (v: number) => Math.max(-1, Math.min(1, v));
    // On phones in either orientation, boost tracking sensitivity 1.5×: the signature
    // swings 1.5× further for the same touch position. "coarse pointer + small in one
    // dimension" targets portrait AND landscape phones (width/height swap on rotation)
    // while excluding tablets and short desktop windows.
    const mq = window.matchMedia('(pointer: coarse) and ((max-width: 500px) or (max-height: 500px))');
    const sens = () => (mq.matches ? 1.5 : 1);
    const onMove = (e: MouseEvent) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      // offset measured from the signature's own center, so it sits "straight"
      // when the cursor is dead-center on the mark.
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      target.current = {
        x: clamp((e.clientX - cx) / (window.innerWidth / 2)) * sens(),
        y: clamp((e.clientY - cy) / (window.innerHeight / 2)) * sens(),
      };
    };
    window.addEventListener('mousemove', onMove, { passive: true });

    // Touch: while a single finger is down (tap or drag) the signature follows
    // it, measured the same way as the cursor. On lift it eases back to straight
    // (target → 0,0) via the same rAF easing below.
    const aimAt = (clientX: number, clientY: number) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      target.current = {
        x: clamp((clientX - cx) / (window.innerWidth / 2)) * sens(),
        y: clamp((clientY - cy) / (window.innerHeight / 2)) * sens(),
      };
    };
    // A tap lasts only a couple of frames — too brief for the easing to visibly
    // reach the finger before release. So we hold the aimed position for a beat
    // after lift, letting even a quick tap swing out fully and then settle back.
    let releaseTimer = 0;
    const HOLD = 180;
    const straighten = () => {
      target.current = { x: 0, y: 0 };
    };
    const onTouch = (e: TouchEvent) => {
      if (e.touches.length !== 1) return; // single finger only
      if (releaseTimer) {
        window.clearTimeout(releaseTimer);
        releaseTimer = 0;
      }
      aimAt(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchEnd = (e: TouchEvent) => {
      // if a finger remains, keep following it; otherwise hold, then straighten
      if (e.touches.length === 1) {
        aimAt(e.touches[0].clientX, e.touches[0].clientY);
        return;
      }
      if (releaseTimer) window.clearTimeout(releaseTimer);
      releaseTimer = window.setTimeout(straighten, HOLD);
    };
    window.addEventListener('touchstart', onTouch, { passive: true });
    window.addEventListener('touchmove', onTouch, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });

    // --- one loop composes parallax (translate) + magnification (scale) ---
    let raf = 0;
    const FRAMES = 4.25; // frame delay added per ring, outward
    const tick = () => {
      coreScale.current += (coreScaleTarget.current - coreScale.current) * 0.3;
      // record the cursor offset so each ring can follow a delayed sample
      const h = history.current;
      h.push({ x: target.current.x, y: target.current.y });
      const maxLen = (RINGS.length - 1) * FRAMES + 4;
      if (h.length > maxLen) h.splice(0, h.length - maxLen);
      ringRefs.current.forEach((ring, i) => {
        if (!ring) return;
        // inner rings follow the live cursor; each outer ring follows the
        // cursor from more frames ago → a pronounced, even staggered trail.
        const delayFrames = Math.round((RINGS.length - 1 - i) * FRAMES);
        const src = h[Math.max(0, h.length - 1 - delayFrames)] || target.current;
        const o = ringOff.current[i];
        o.x += (src.x - o.x) * 0.3;
        o.y += (src.y - o.y) * 0.3;
        ringScale.current[i] += (ringScaleTarget.current[i] - ringScale.current[i]) * 0.3;
        const depth = ((i + 1) / RINGS.length) * 34;
        const tx = o.x * depth + 340;
        const ty = o.y * depth + 340;
        const s = ringScale.current[i].toFixed(3);
        ring.setAttribute('transform', `translate(${tx.toFixed(2)} ${ty.toFixed(2)}) scale(${s}) translate(-340 -340)`);
      });
      if (coreRef.current) {
        coreOff.current.x += (target.current.x - coreOff.current.x) * 0.3;
        coreOff.current.y += (target.current.y - coreOff.current.y) * 0.3;
        const cx = (coreOff.current.x * 20 + 340).toFixed(2);
        const cy = (coreOff.current.y * 20 + 340).toFixed(2);
        const s = coreScale.current.toFixed(3);
        coreRef.current.setAttribute('transform', `translate(${cx} ${cy}) scale(${s}) translate(-340 -340)`);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    // --- click ripple: core → innermost ring → ... → outermost ring ---
    const STEP = 60;
    const MAG = 1.09;
    // Flash via inline style (so var(--sig-flash) resolves); rest clears it so
    // the base stroke/fill (currentColor + attribute opacity) takes over again.
    const lit = (el: SVGPolygonElement, isCore: boolean, op: number) => {
      if (isCore) {
        el.style.fill = 'var(--sig-flash)';
        el.style.fillOpacity = op.toFixed(2);
      } else {
        el.style.stroke = 'var(--sig-flash)';
        el.style.strokeOpacity = op.toFixed(2);
      }
    };
    const rest = (el: SVGPolygonElement, isCore: boolean) => {
      if (isCore) {
        el.style.fill = '';
        el.style.fillOpacity = '';
      } else {
        el.style.stroke = '';
        el.style.strokeOpacity = '';
      }
    };
    const setScale = (item: RippleItem, v: number) => {
      if (item.isCore) coreScaleTarget.current = v;
      else ringScaleTarget.current[item.ringIndex] = v;
    };
    const onClick = () => {
      if (rippling.current) return;
      const order: RippleItem[] = [{ el: coreRef.current, isCore: true, ringIndex: -1 }];
      for (let i = RINGS.length - 1; i >= 0; i--) {
        order.push({ el: ringRefs.current[i], isCore: false, ringIndex: i });
      }
      rippling.current = true;
      order.forEach((item, k) => {
        window.setTimeout(() => {
          const base = item.isCore ? 0.5 : baseOpacity(item.ringIndex);
          // taper the boost outward so the large outer rings don't over-flash
          const boost = item.isCore ? 3.5 : 2.0 + (item.ringIndex / (RINGS.length - 1)) * 1.5;
          if (item.el) lit(item.el, item.isCore, Math.min(base * boost, 1));
          setScale(item, MAG);
          if (k > 0) {
            const prev = order[k - 1];
            if (prev.el) rest(prev.el, prev.isCore);
            setScale(prev, 1);
          }
          if (k === order.length - 1) {
            window.setTimeout(() => {
              if (item.el) rest(item.el, item.isCore);
              setScale(item, 1);
              rippling.current = false;
            }, STEP + 140);
          }
        }, k * STEP);
      });
    };
    document.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchstart', onTouch);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      if (releaseTimer) window.clearTimeout(releaseTimer);
      cancelAnimationFrame(raf);
      document.removeEventListener('click', onClick);
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 680 680"
      aria-hidden={true}
      style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible', color: 'var(--sig-color)' }}
    >
      {RINGS.map((v, i) => (
        <polygon
          key={v}
          ref={(el) => {
            ringRefs.current[i] = el;
          }}
          points={diamondPoints(v)}
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
          strokeOpacity={baseOpacity(i)}
          style={{ transition: 'stroke 0.18s ease, stroke-opacity 0.18s ease' }}
        />
      ))}
      <polygon
        ref={coreRef}
        points="340,322 358,340 340,358 322,340"
        fill="currentColor"
        fillOpacity={0.5}
        style={{ transition: 'fill 0.18s ease, fill-opacity 0.18s ease' }}
      />
    </svg>
  );
};

export default HeroSignature;
